import React, { useState } from 'react';
import { MapPin, Clock, Phone, Mail, Feather, Send } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  const TELEGRAM_BOT_TOKEN = '7792080768:AAGdZ6jv4H4ueprl4aPJpgXRSmuGaxfxcxo';
  const TELEGRAM_CHAT_ID = '1523610682';

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);

    try {
      const message = `📧 New Newsletter Subscriber\n\nEmail: ${email}\nSource: WriteToSite.com Footer`;

      const response = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to subscribe');
      }

      alert('Thank you for subscribing! Welcome to our community.');
      setEmail('');
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const quickLinks = [
    { label: 'Services', href: '/services' },
    { label: 'Demo Preview', href: '/demo-preview' },
    { label: 'Pricing', href: '/services#pricing' },
    { label: 'Writer\'s Journal', href: '/blog' },
    { label: 'Author Spotlight', href: '/author-spotlight' },
  ];

  const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Service', href: '/terms-of-use' },
    { label: 'FAQs', href: '/faqs' },
  ];

  return (
    <footer className="relative bg-ink-950 text-cream-100 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-ink-800/50 rounded-full blur-3xl" />
      </div>

      {/* Gold Top Border */}
      <div className="h-px bg-gradient-to-r from-transparent via-gold-500/50 to-transparent" />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8">
            {/* Brand Column */}
            <div className="lg:col-span-4">
              <Link to="/" className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-gold-500/10 rounded-lg">
                  <Feather className="w-6 h-6 text-gold-400" />
                </div>
                <div>
                  <span className="font-display text-2xl font-bold text-cream-50">WriteToSite</span>
                  <span className="block text-xs font-sans tracking-[0.15em] uppercase text-cream-400">
                    Author Websites
                  </span>
                </div>
              </Link>

              <p className="font-body text-cream-400 leading-relaxed mb-8 max-w-sm">
                Crafting bespoke digital spaces for authors — where your stories captivate,
                your voice resonates, and readers become devoted fans.
              </p>

              {/* Contact Info */}
              <div className="space-y-3">
                <a href="mailto:sales@writetosite.com" className="flex items-center gap-3 text-cream-400 hover:text-gold-400 transition-colors">
                  <Mail className="w-4 h-4 text-gold-500" />
                  <span className="text-sm">sales@writetosite.com</span>
                </a>
                <a href="tel:+18885224733" className="flex items-center gap-3 text-cream-400 hover:text-gold-400 transition-colors">
                  <Phone className="w-4 h-4 text-gold-500" />
                  <span className="text-sm">(888) 522-4733</span>
                </a>
                <div className="flex items-start gap-3 text-cream-400">
                  <MapPin className="w-4 h-4 text-gold-500 mt-0.5" />
                  <span className="text-sm">30 N Gould Street, Ste R<br />Sheridan, WY 82801</span>
                </div>
                <div className="flex items-center gap-3 text-cream-400">
                  <Clock className="w-4 h-4 text-gold-500" />
                  <span className="text-sm">Mon-Fri: 9:00 AM - 6:00 PM EST</span>
                </div>
              </div>
            </div>

            {/* Links Columns */}
            <div className="lg:col-span-2">
              <h3 className="font-display text-lg font-semibold text-cream-50 mb-6">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link, i) => (
                  <li key={i}>
                    <Link
                      to={link.href}
                      className="text-cream-400 hover:text-gold-400 transition-colors text-sm flex items-center gap-2 group"
                    >
                      <span className="w-0 group-hover:w-2 h-px bg-gold-500 transition-all duration-300" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-2">
              <h3 className="font-display text-lg font-semibold text-cream-50 mb-6">Legal</h3>
              <ul className="space-y-3">
                {legalLinks.map((link, i) => (
                  <li key={i}>
                    <Link
                      to={link.href}
                      className="text-cream-400 hover:text-gold-400 transition-colors text-sm flex items-center gap-2 group"
                    >
                      <span className="w-0 group-hover:w-2 h-px bg-gold-500 transition-all duration-300" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter Column */}
            <div className="lg:col-span-4">
              <h3 className="font-display text-lg font-semibold text-cream-50 mb-6">Stay Inspired</h3>
              <p className="text-cream-400 text-sm mb-6 leading-relaxed">
                Join our community of authors. Get insights on building your online presence,
                marketing tips, and exclusive resources.
              </p>

              <form onSubmit={handleSubscribe} className="relative">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-5 py-4 pr-14 bg-ink-900 border border-ink-700 text-cream-100 placeholder:text-ink-500 focus:border-gold-500 focus:outline-none transition-colors font-body text-sm"
                  required
                />
                <button
                  type="submit"
                  disabled={isSubscribing}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-gold-500 text-ink-900 hover:bg-gold-400 transition-colors disabled:opacity-50"
                >
                  {isSubscribing ? (
                    <div className="w-4 h-4 border-2 border-ink-900 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </form>

              <p className="text-cream-500 text-xs mt-3">
                By subscribing, you agree to our privacy policy. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-8 border-t border-ink-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-cream-500 text-sm font-sans">
              © {new Date().getFullYear()} WriteToSite. All rights reserved.
            </div>

            <div className="flex items-center gap-2 text-cream-500 text-sm">
              <span>Crafted with</span>
              <Feather className="w-4 h-4 text-gold-500" />
              <span>by</span>
              <a
                href="https://mysitebroker.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold-400 hover:text-gold-300 transition-colors"
              >
                MySiteBroker
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
